import moment from 'moment';
import get from 'lodash/get';
import { useSelector } from 'react-redux';
import { MENU as ENTITY } from 'bento-ordering/menus/constants/entities';
import { getItemByIdSelector } from 'bento-ordering/base/reducers/bentoApi';
import { generatePath } from 'bento-ordering/utils/generatePath';
import { getOnlineOrderingUrlParams } from 'bento-ordering/utils/url';
import { getAreRequiredFieldsSelected } from 'helpers/forms/addToCartCta';
import { getMenuItem } from 'bento-ordering/menus/reducers/menuItemsDetail';
import { getQuantityOfProductInCart } from 'bento-ordering/cart/selectors';
import { getProductsWithInventoryForLocation } from 'bento-ordering/locations/reducers/locationInventory';
import { combineDateWithCloseTime } from 'bento-ordering/base/utils/timeHelpers';
import { getMenuItemsModifierAndOptionNames } from 'bento-ordering/menus/utils/getMenuItemsModifierAndOptionNames';
import {
  getLocationMenuForLaterOrNextSelector,
  getLocationMenuForAsapOrFirstLaterSelector
} from 'bento-ordering/locations/selectors';

// called on root state
export const getIsEnabledAddToCartSelector = (state, modifierGroups) => {
  const { form, router, orderAmount } = state;
  const { menuItemSlug } = getOnlineOrderingUrlParams(router.location.pathname);
  const menuItem = getMenuItem(state, menuItemSlug);
  const values = get(form, 'menuItemDetail.values', {});

  const variants = menuItem.variants;
  const shouldCheckProductVariantsField = getShouldRenderProductVariantsSection(
    variants
  );

  const requiredFieldsAreSelected = getAreRequiredFieldsSelected(
    values,
    modifierGroups,
    shouldCheckProductVariantsField
  );

  // we assume an item is available unless is available has set to false
  const isMenuItemAvailable = menuItem.is_available !== false;
  const isMaximumExceeded = orderAmount.isMaximumExceeded;
  return {
    requiredFieldsAreSelected,
    isMaximumExceeded,
    isMenuItemAvailable,
    canAddToCart: !!(
      requiredFieldsAreSelected &&
      !isMaximumExceeded &&
      isMenuItemAvailable
    )
  };
};

/**
  get if inventory applies to the product given pwi and requested_fulfillment_is_same_day
  @param {object} pwi (product with inventory object)
  @param {object} cartState (cart object)
  @return {boolean} representing if inventory should count on
  for menu given fulfillment options and pwi date/count future fields
*/
export const getDoesInventoryApply = (pwi, cartState) => {
  const requestedFulfillmentIsSameDay =
    cartState.requested_fulfillment_is_same_day;

  const shouldCountInventoryForFulfillmentDate =
    pwi.count_future_day_orders || requestedFulfillmentIsSameDay;

  const inventoryIsLowAtFulfillmentDatetime =
    pwi.inventory !== null &&
    (pwi.reset_time === null ||
      moment
        .utc(pwi.reset_time)
        .isAfter(moment.utc(cartState.requested_fulfillment_datetime_as_utc)));

  return (
    shouldCountInventoryForFulfillmentDate &&
    inventoryIsLowAtFulfillmentDatetime
  );
};

/**
 * get inventory for product of id
  @param {object} productsWithInventory (product with inventory object)
  @param {number} productId
  @param {object} cartState (cart object)
  @return {number} representing available inventory (or null if inventory does not apply)
*/
export const getInventoryForProductId = (
  productsWithInventory,
  productId,
  cartState
) => {
  const product = productsWithInventory.find(pwi => {
    return pwi.product === productId;
  });
  if (!product) {
    return null;
  }
  const doesInventoryApply = selectorsModule.getDoesInventoryApply(
    product,
    cartState
  );
  return doesInventoryApply ? product.inventory : null;
};

/**
 * get inventory for item
 * @param {object} item (menuItem obj)
 * @param {array} productsWithInventory (arr of pwi objects)
 * @param {array} soldOutProducts (arr of Sold Out product ids (integers))
 * @param {object} cart (cart object)
 * @return {boolean} representing if inventory should count on
 * for menu given fulfillment options and pwi date/count future fields

 * process item to return Sold Out status and inventory values
*/
export const getItemWithInventorySelector = (
  item,
  productsWithInventory,
  cart
) => {
  const inventory = selectorsModule.getInventoryForProductId(
    productsWithInventory,
    item.product.id,
    cart
  );
  let inventoryLessCart = null;
  if (inventory !== null) {
    const qtyInCart = getQuantityOfProductInCart(cart, item.product.id);
    inventoryLessCart = inventory - qtyInCart;
  }

  return {
    ...item,
    inventory,
    inventoryLessCart
  };
};

/**
 * get inventory plus modifiers and options names for item
 * @param {object} item (menuItem obj)
 * @param {array} productsWithInventory (arr of pwi objects)
 * @param {object} cart (cart object)
 * @param {object} menuItemsModifierAndOptionNames (obj, keys: product ids, values: arrays of strings(modifier + option names))
 * @return {object} item combined with inventory, deduped modifiers and options names
 */
export const getItemWithInventoryAndModifierNamesSelector = (
  item,
  productsWithInventory,
  cart,
  menuItemsModifierAndOptionNames
) => {
  const itemWithModifierNames = {
    ...item,
    modifierAndOptionNames:
      menuItemsModifierAndOptionNames[item.product.id] || []
  };
  return selectorsModule.getItemWithInventorySelector(
    itemWithModifierNames,
    productsWithInventory,
    cart
  );
};

/**
 * get menu with inventory for all items
  @param {object} state (root of redux state)

  @return {object} menu with items combined with inventory
*/
export const getMenuWithInventorySelector = state => {
  const productsWithInventory =
    getProductsWithInventoryForLocation(
      state.locationInventory,
      state.cart.online_ordering_location
    ) || [];

  return {
    ...state.menu,
    sections: (state.menu.sections || []).map(section => ({
      ...section,
      items: section.items.map(item =>
        selectorsModule.getItemWithInventorySelector(
          item,
          productsWithInventory,
          state.cart
        )
      )
    }))
  };
};

/**
 * get menu with inventory plus modifiers and options names for all items
 * modifier names are added to aid a better searching menu experience
  @param {object} state (root of redux state)
  @return {object} menu with items combined with inventory, plus deduped modifiers and options names
*/

export const getMenuWithInventoryAndModifierNamesSelector = state => {
  const productsWithInventory =
    getProductsWithInventoryForLocation(
      state.locationInventory,
      state.cart.online_ordering_location
    ) || [];

  const menuItemsModifierAndOptionNames = Object.keys(
    state.menuItemsDetail?.byId ?? {}
  ).length
    ? getMenuItemsModifierAndOptionNames(state.menuItemsDetail.byId)
    : {};

  return {
    ...state.menu,
    sections: (state.menu.sections || []).map(section => ({
      ...section,
      items: section.items.map(item =>
        selectorsModule.getItemWithInventoryAndModifierNamesSelector(
          item,
          productsWithInventory,
          state.cart,
          menuItemsModifierAndOptionNames
        )
      )
    }))
  };
};

/**
 * getBaseMenuPathForLocation
  @param {object} state (root of redux state)

  @return {string} path of menu (defaults to root if location not found)
*/
export const getBaseMenuPathForLocation = state => {
  const { cart, restaurantLocations } = state;
  const id = cart.online_ordering_location;
  const selectedLocationSlug = get(
    getItemByIdSelector(restaurantLocations, id),
    'location.slug'
  );
  if (selectedLocationSlug) {
    return generatePath(ENTITY, 'menu', { slug: selectedLocationSlug });
  } else {
    return '/';
  }
};

/**
 * Takes global state, and returns time the currently active menu expires
 * Used to determine when a menu has expired
 * @param {object} state global state object
 *
 * @returns {string} representing the datetime of when menu expires
 */
export const getLocationMenuCloseDateTimeSelector = state => {
  let locationMenu = null;
  // double check that cart is always valid here vs workingCopy
  const { cart, restaurantLocations, locationThrottledTimes } = state;

  if (cart.requested_fulfillment_is_asap) {
    locationMenu = getLocationMenuForAsapOrFirstLaterSelector(
      restaurantLocations,
      cart,
      locationThrottledTimes
    );
  } else {
    // may need to add location throttled times here
    locationMenu = getLocationMenuForLaterOrNextSelector(
      restaurantLocations,
      cart
    );
  }
  const closeTime = locationMenu
    ? combineDateWithCloseTime(locationMenu.date, locationMenu.close_time)
    : '';

  return closeTime;
};

/**
 * Takes state for restaurantLocations and cart and returns the id of the
 * active menu based on the cart's fulfillment options.
 * Used to fetch menu and menu details
 * @param {object} restaurantLocations restaurantLocations state object
 * @param {object} cart cart state object
 * @param {object} locationThrottledTimes from state
 * @returns {number} menu id
 */
export const getMenuIdForFulfillmentOptions = (
  restaurantLocations,
  cart,
  locationThrottledTimes
) => {
  let menuId = null;
  if (cart.requested_fulfillment_is_asap) {
    const locationMenu =
      getLocationMenuForAsapOrFirstLaterSelector(
        restaurantLocations,
        cart,
        locationThrottledTimes
      ) || {};

    menuId = locationMenu.menu_id;
  } else {
    const locationMenu =
      getLocationMenuForLaterOrNextSelector(restaurantLocations, cart) || {};
    menuId = locationMenu.menu_id;
  }

  return menuId;
};

/**
 * Takes state for cart and uses the productId of the last item added to cart
 * to return the image url of a specified product added to the cart
 * Used to fetch menu and menu details
 * @param {object} state (root of redux state)
 * @param {object} cart cart state object
 * @param {object} byId from state
 * @returns {string} image url
 */

export const selectImageUrlForAddedCartItem = state => {
  const { cart, menuItemsDetail } = state;
  const lastCartItemIndex = cart.items.length - 1;
  if (lastCartItemIndex < 0) {
    // return empty array if no items in cart to satisfy prop types
    return [];
  }
  const productId = cart?.items[lastCartItemIndex]?.product;

  return menuItemsDetail.byId[productId].images[0];
};

export const useGetLocationId = () =>
  useSelector(state => state.cart.online_ordering_location);

export const useGetLocations = () =>
  useSelector(state => state.restaurantLocations);

export const useSelectedLocationSlug = () =>
  get(
    getItemByIdSelector(useGetLocations(), useGetLocationId()),
    'location.slug'
  );

export const removeDuplicateProducts = items => {
  const ids = items.map(item => item.product.id);
  return items.filter(
    ({ product }, index) => !ids.includes(product.id, index + 1)
  );
};

export const useGetCartItems = () =>
  useSelector(state => state.cart.items.map(item => item.product));

export const useGetMenuItems = () =>
  removeDuplicateProducts(
    useSelector(getMenuWithInventorySelector)
      .sections.map(section => section.items)
      .flat()
  );

export const useGetPopularItemsIds = () =>
  useSelector(state => state.menu.popular_entree_items) || [];

export const useGetMenuPopularItemsNotInCart = () => {
  const [cartItems, menuItems, popularItemsIds] = [
    useGetCartItems(),
    useGetMenuItems(),
    useGetPopularItemsIds()
  ];

  const popularItems = menuItems.filter(
    item =>
      item.inventory !== 0 &&
      popularItemsIds.includes(item.product.id) &&
      !cartItems.includes(item.product.id)
  );

  return popularItems;
};

export const getShouldRenderProductVariantsSection = variants =>
  variants?.length > 1;

// using this pattern to be able to easily mock parts of the module
// in jest, but that means any functions in here that use
// other functions defined below should use the exported module version
const selectorsModule = {
  getIsEnabledAddToCartSelector,
  getDoesInventoryApply,
  getInventoryForProductId,
  getMenuWithInventorySelector,
  getMenuWithInventoryAndModifierNamesSelector,
  getBaseMenuPathForLocation,
  getItemWithInventorySelector,
  getItemWithInventoryAndModifierNamesSelector,
  getLocationMenuCloseDateTimeSelector,
  getMenuIdForFulfillmentOptions,
  getShouldRenderProductVariantsSection,
  useGetLocationId,
  useGetLocations,
  useSelectedLocationSlug,
  selectImageUrlForAddedCartItem
};

export default selectorsModule;
